import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';
import VendorFlow from './pages/VendorFlow';
import './styles/index.css';

/**
 * Two surfaces, one origin.
 *
 *   /v/:siteId   the vendor flow — public, no MSAL, no auth library loaded
 *   /staff/*     the staff console — MSAL-gated
 *
 * The staff console is lazy-loaded so a vendor never downloads MSAL. That is
 * not a micro-optimisation: MSAL plus the console is the larger half of this
 * app, and the vendor half is used by people on a phone at a gate, often on a
 * weak signal, who are already mildly annoyed at having to do this at all.
 */

const StaffApp = lazy(() => import('./staff/StaffApp'));

function VendorRoute(): JSX.Element {
  const { siteId } = useParams<{ siteId: string }>();
  if (!siteId) return <Navigate to="/" replace />;
  return (
    <div className="vs-shell">
      <VendorFlow siteId={decodeURIComponent(siteId)} />
    </div>
  );
}

function Landing(): JSX.Element {
  return (
    <div className="vs-plain">
      <img
        src="/assets/logo-navy.png"
        alt="US Storage Centers"
        style={{ height: 30, marginBottom: 8 }}
      />
      <h1>Vendor check-in</h1>
      <p>
        Scan the QR sign at the site office or gate to sign in or out. Each site has its own
        sign — there is no general link.
      </p>
      <a
        href="/staff"
        style={{
          marginTop: 8,
          fontSize: 13.5,
          fontWeight: 600,
          color: 'var(--us-blue)',
          textDecoration: 'none',
        }}
      >
        Staff sign-in →
      </a>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/v/:siteId" element={<VendorRoute />} />
        <Route
          path="/staff/*"
          element={
            <Suspense
              fallback={
                <div className="console-plain">
                  <div className="vs-spinner" style={{ color: 'var(--us-blue)' }} />
                </div>
              }
            >
              <StaffApp />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
);
